interface ChunkFailureFallbackOptions {
  /** Called when the user taps Reload — e.g. to clear the loop-attempt counter. */
  onRetry?: () => void
}

const FALLBACK_ID = 'chunk-failure-fallback'

// Polish copy mirrors the app's existing version/error strings
// (version.reload = "Przeładuj", error.somethingWrong = "Coś poszło nie tak").
// Hardcoded on purpose — see the dependency note below.
const HEADING = 'Coś poszło nie tak'
const MESSAGE =
  'Nie udało się załadować aplikacji. Sprawdź połączenie z internetem i spróbuj ponownie.'
const RELOAD = 'Przeładuj'

/**
 * Last-resort, dependency-free error screen shown when the app's JS chunks keep
 * failing to load and the reload loop has been stopped.
 *
 * Rendered with plain DOM + inline styles ON PURPOSE: we only get here because
 * dynamically-imported modules cannot be fetched, so the React ErrorPage route
 * (and its lazily-loaded icon chunks) would fail to render too. This screen must
 * depend on nothing but `document`, or it could blank out just like everything
 * else and the user would be left staring at the void.
 */
export function renderChunkFailureFallback({ onRetry }: ChunkFailureFallbackOptions = {}): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(FALLBACK_ID)) return // idempotent — repeated errors don't stack overlays

  const overlay = document.createElement('div')
  overlay.id = FALLBACK_ID
  overlay.setAttribute('role', 'alert')
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:16px',
    'padding:24px',
    'text-align:center',
    'background:#ffffff',
    'color:#0a0a0a',
    "font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
  ].join(';')

  const heading = document.createElement('h1')
  heading.textContent = HEADING
  heading.style.cssText = 'margin:0;font-size:20px;font-weight:600'

  const message = document.createElement('p')
  message.textContent = MESSAGE
  message.style.cssText = 'margin:0;max-width:32rem;font-size:15px;line-height:1.5;color:#52525b'

  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = RELOAD
  button.style.cssText = [
    'margin-top:8px',
    'padding:10px 24px',
    'font-size:15px',
    'font-weight:500',
    'cursor:pointer',
    'border:none',
    'border-radius:8px',
    'background:#0a0a0a',
    'color:#ffffff',
  ].join(';')
  button.addEventListener('click', () => {
    try {
      onRetry?.()
    } catch {
      // A failing retry hook must not block the reload.
    }
    // Reload to a clean URL — strip the loop's ?_reload marker so the next boot
    // starts fresh rather than re-entering with the looping query param.
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('_reload')
      window.location.replace(url.toString())
    } catch {
      window.location.reload()
    }
  })

  overlay.appendChild(heading)
  overlay.appendChild(message)
  overlay.appendChild(button)

  const mount = () => {
    if (document.body && !document.getElementById(FALLBACK_ID)) {
      document.body.appendChild(overlay)
    }
  }
  if (document.body) mount()
  else document.addEventListener('DOMContentLoaded', mount, { once: true })
}
